import { Component } from '@theme/component';
import { AttributionBackendAPIClient } from '@theme/attribution';
import { fetchConfig } from '@theme/utilities';

const SHARED_CART_TOAST_LOADING = 'Preparing your cart...';
const SHARED_CART_TOAST_SUCCESS = 'Cart restored from shared link.';
const SHARED_CART_TOAST_ERROR = 'Failed to restore shared cart.';
const SHARED_CART_TOAST_EMPTY_CART = 'This shared cart has no items.';

/**
 * A custom class that handles a shared cart.
 */
class SharedCart {
  /** 
   * @type {any} 
   */
  sharedCart = null;

  constructor() {
    const sharedCartID = this.#getURLSharedCartID();
    if (sharedCartID) this.#restoreSharedCart(sharedCartID);
  }

  /**
   * Gets the shared cart ID from URL.
   */
  #getURLSharedCartID() {
    const urlParams = new URLSearchParams(window.location.search);

    return urlParams.get('shareCartId');
  }

  /**
   * Clears shared cart ID from URL.
   */
  #clearURLSharedCartID() {
    const url = new URL(window.location.href);

    url.searchParams.delete('shareCartId');

    window.history.replaceState({}, document.title, url.toString());
  }

  /**
   * Fetches shared cart.
   * @param {String} sharedCartID - The shared cart ID.
   */
  async #getSharedCart(sharedCartID) {
    const response = await fetch(
      `/apps/yl-storefront/api/share-cart/${encodeURIComponent(sharedCartID)}`
    );
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  }

  /**
   * Rolls back to a previous cart.
   * @param {any} cart - The cart to restore.
   */
  async #restorePreviousCart(cart) {
    if (!cart?.items?.length) return;

    try {
      const items = cart.items.map((/** @type {any} */ item) => ({ id: item.variant_id, quantity: item.quantity }));
      const config = fetchConfig('json', {
        body: JSON.stringify({ items })
      });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
    } catch (e) {
      console.error('[SharedCart] CRITICAL: Failed to restore previous cart.', e);
    }
  }

  /**
   * Clears current cart items.
   */
  async #clearExistingCartItems() {
    const response = await fetch('/cart/clear.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' }
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
  }

  /**
   * Adds items from the shared cart to the current cart.
   */
  async #addItemsFromSharedCart() {
    const sharedCartItems = Array.isArray(this.sharedCart?.items) ? this.sharedCart.items : [];
    if (!sharedCartItems.length) return;

    const items = sharedCartItems.map((/** @type {any} */ item) => ({
      id: item.variantId,
      quantity: item.quantity,
      properties: {
        _loyalty_type: 'one-time'
      }
    }));
    const config = fetchConfig('json', { 
      body: JSON.stringify({ items }) 
    });
    const response = await fetch(Theme.routes.cart_add_url, { 
      ...config 
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
  }

  /**
   * Sets cart attributes from the shared cart for the current cart.
   */
  async #setAttributesFromSharedCart() {
    const enrollerId = this.sharedCart?.enrollerId;
    const sponsorId = this.sharedCart?.sponsorId;
    if (!enrollerId && !sponsorId) return;

    const attributes = { enrollerId, sponsorId };
    const config = fetchConfig('json', { 
      body: JSON.stringify({ attributes }) 
    });
    const response = await fetch(Theme.routes.cart_update_url, { 
      ...config 
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
  }

  /**
   * Restores a shared cart.
   * @param {String} sharedCartID - The shared cart ID.
   */
  async #restoreSharedCart(sharedCartID) {
    try {
      /** @type {any} */ (window)?.toast?.info?.(SHARED_CART_TOAST_LOADING);

      this.sharedCart = await this.#getSharedCart(sharedCartID);
      const sharedCartItems = Array.isArray(this.sharedCart?.items) ? this.sharedCart.items : [];
      if (!sharedCartItems.length) {
        this.#clearURLSharedCartID();
        /** @type {any} */ (window)?.toast?.info?.(SHARED_CART_TOAST_EMPTY_CART);
        return;
      }

      const previousCart = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());

      await this.#clearExistingCartItems();
      
      try {
        await this.#addItemsFromSharedCart();
      } catch (e) {
        console.error('[SharedCart] Failed to add shared cart items, restoring previous cart.', e);
        await this.#restorePreviousCart(previousCart);
        throw e;
      }

      await this.#setAttributesFromSharedCart();

      this.#clearURLSharedCartID();
      /** @type {any} */ (window)?.toast?.success?.(SHARED_CART_TOAST_SUCCESS);
      window.location.href = '/cart';

    } catch (e) {
      console.error('[SharedCart] Failed to restore shared cart.', e);
      /** @type {any} */ (window)?.toast?.error?.(SHARED_CART_TOAST_ERROR);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new SharedCart();
})

/**
 * A custom element that displays a share modal.
 * 
 * @typedef {object} Refs
 * @property {HTMLInputElement} sponsorIdInput - The sponsor ID input element.
 * @property {HTMLInputElement} enrollerIdInput - The enroller ID input element.
 * @property {HTMLElement} sponsorIdLabel - The sponsor ID label text element.
 * @property {HTMLElement} enrollerIdLabel - The sponsor ID label text element.
 * @property {HTMLElement} attributionErrorContainer - The attribution error container element.
 * @property {HTMLElement} shareContent - The modal content blurred while the share link loads.
 * @property {HTMLElement[]} shareActions - The share buttons and copy link containers.
 * @property {HTMLElement} shareLinkError - The share link error message element.
 *
 * @extends {Component<Refs>}
 */
export class ShareModal extends Component {
  requiredRefs = ['sponsorIdInput', 'enrollerIdInput'];

  /**
   * @type {AttributionBackendAPIClient}
   */
  backendClient = new AttributionBackendAPIClient();
  /**
   * @type {string}
  */
  sponsorId = '';
  /**
   * @type {string}
  */
  enrollerId = '';
  /**
   * @type {string}
  */
  shareCartId = '';
  /**
   * @type {boolean}
  */
  isShareLinkReady = true;
  /**
   * @type {MutationObserver | null}
  */
  #openStateObserver = null;
  /**
   * @type {boolean}
  */
  #isOpen = false;
  /**
   * Increments on every save so a slow response from an earlier open can't overwrite a newer one.
   * @type {number}
   */
  #saveRequestId = 0;

  /**
   * Sponsor + enroller must both be non-empty (trimmed). Matches Liquid: share UI only when yl_member_id is set.
   * @returns {boolean}
   */
  #hasValidAttributionIds() {
    const s = typeof this.sponsorId === 'string' ? this.sponsorId.trim() : '';
    const e = typeof this.enrollerId === 'string' ? this.enrollerId.trim() : '';
    return Boolean(s && e);
  }

  get sponsorIdInput() {
    return this.refs.sponsorIdInput;
  }
  get enrollerIdInput() {
    return this.refs.enrollerIdInput;
  }
  get shareFrom() {
    return this.dataset.shareFrom;
  }

  async connectedCallback() {
    super.connectedCallback();

    const sponsorRaw = this.sponsorIdInput?.value?.trim() ?? '';
    const enrollerRaw = this.enrollerIdInput?.value?.trim() ?? '';
    if (!sponsorRaw || !enrollerRaw) return;

    this.sponsorId = sponsorRaw;
    this.enrollerId = enrollerRaw;

    if (this.shareFrom === 'cart') {
      // openModal() only toggles .is-open, so watch for it to save the cart on each open.
      this.#openStateObserver = new MutationObserver(() => this.#handleOpenStateChange());
      this.#openStateObserver.observe(this, { attributes: true, attributeFilter: ['class'] });
      // share.js loads as a module, so the modal may already be open before the observer exists.
      this.#handleOpenStateChange();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#openStateObserver?.disconnect();
    this.#openStateObserver = null;
  }

  #handleOpenStateChange() {
    const isOpen = this.classList.contains('is-open');
    if (isOpen === this.#isOpen) return;

    this.#isOpen = isOpen;
    if (isOpen) this.#saveSharedCart();
  }

  /**
   * Saves the current cart and blocks sharing until the link includes its ID.
   */
  async #saveSharedCart() {
    const requestId = ++this.#saveRequestId;
    this.shareCartId = '';
    this.#setShareLinkState('loading');

    try {
      const shareCartId = await this.getShareCartId();
      if (requestId !== this.#saveRequestId) return;

      this.shareCartId = shareCartId;
      this.#setShareLinkState('ready');
    } catch (err) {
      if (requestId !== this.#saveRequestId) return;

      console.error('[ShareModal] Failed to save shared cart.', err);
      this.#setShareLinkState('error');
    }
  }

  /**
   * @param {'loading' | 'ready' | 'error'} state - The share link state.
   */
  #setShareLinkState(state) {
    const { shareContent, shareActions, shareLinkError } = this.refs;
    const isLoading = state === 'loading';
    const hasError = state === 'error';

    this.isShareLinkReady = state === 'ready';

    if (shareContent) {
      shareContent.classList.toggle('loading-state', isLoading);
      shareContent.toggleAttribute('inert', isLoading);
      shareContent.setAttribute('aria-busy', String(isLoading));
    }

    shareActions?.forEach((element) => element.toggleAttribute('inert', hasError));

    if (shareLinkError) {
      shareLinkError.textContent = hasError ? shareLinkError.dataset.message ?? '' : '';
    }
  }

  /**
   * Sanitizes input value to numerics only.
   * @param {Event} event - The input event.
   */
  handleSanitizeInput(event) {
    const targetInput = /** @type {HTMLInputElement} */ (event.target);
    const sanitized = targetInput.value.replace(/\D/g, '');
    if (targetInput.value !== sanitized) targetInput.value = sanitized;
  }

  /**
   * Builds the shareable link.
   */
  buildShareLink() {
    const shareBaseUrl = this.dataset.shareUrl || window.location.href;
    let shareLink = new URL(shareBaseUrl);
  
    if (this.sponsorId) shareLink.searchParams.set('sponsorId', this.sponsorId);
    if (this.enrollerId) shareLink.searchParams.set('enrollerId', this.enrollerId);
    if (this.shareCartId) shareLink.searchParams.set('shareCartId', this.shareCartId);
    
    return shareLink.href;
  }

  /**
   * Saves the current cart and returns its shareable ID, or '' when there's nothing to share.
   * @returns {Promise<string>}
   */
  async getShareCartId() {
    if (!this.#hasValidAttributionIds()) return '';

    // Same request as fetchCartJSON in global-cart.js (locale-aware root, no cache), but throws
    // instead of falling back to the possibly stale server-rendered cart.
    const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      credentials: 'same-origin',
      cache: 'no-store'
    });
    if (!cartResponse.ok) throw new Error(`HTTP ${cartResponse.status}`);

    const cart = await cartResponse.json();
    if (!cart?.items?.length) return '';

    const itemsData = cart.items.map((/** @type {any} */ item) => {
      return {
        variantId: item.variant_id,
        quantity: item.quantity
      }
    });

    const payload = {
      items: itemsData,
      sponsorId: this.sponsorId,
      enrollerId: this.enrollerId
    };

    const response = await fetch(`/apps/yl-storefront/api/share-cart`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const result = await response.json();
    if (!result?.cartId) throw new Error('Share cart response is missing cartId');

    return result.cartId;
  }

  /**
   * Handles the copy link click event.
   * @param {Event} event - The click event.
   */
  handleCopyLink(event) {
    if (!this.isShareLinkReady) return;

    const target = /** @type {HTMLElement} */ (event.target);
    const button = target.closest('button');

    const shareLink = this.buildShareLink();

    navigator.clipboard.writeText(shareLink).then(() => {
      if (!button) return;
      const buttonDefaultText = button.querySelector('[data-button-text="default"]');
      const buttonSuccessText = button.querySelector('[data-button-text="success"]');
      if (!buttonDefaultText || !buttonSuccessText) return;

      buttonDefaultText.classList.add('hidden');
      buttonSuccessText.classList.remove('hidden');
      setTimeout(() => {
        buttonSuccessText.classList.add('hidden');
        buttonDefaultText.classList.remove('hidden');
      }, 2000);
    }).catch((err) => {
      console.error('Failed to copy link:', err);
    });
  }

  /**
   * Handles the share link click event.
   * @param {{ platform: string }} data - The data object.
   */
  handleShareLink(data) {
    if (!this.isShareLinkReady) return;

    const { platform } = data;
    const shareTagline = this.dataset.shareTagline || '';
    const tagline = encodeURIComponent(shareTagline);
    const shareLink = encodeURIComponent(this.buildShareLink());
    const isMobileOrTablet = /android|iphone|ipad|mobile/i.test(navigator.userAgent) && navigator.maxTouchPoints > 0;
    let permalink = '';

    switch(platform) {
      case 'facebook':
        permalink = `https://www.facebook.com/sharer/sharer.php?u=${shareLink}`;
        break;
      case 'twitter':
        permalink = `https://twitter.com/intent/tweet?url=${shareLink}&text=${tagline}`;
        break;
      case 'messenger':
        if (isMobileOrTablet) {
          permalink = `fb-messenger://share/?link=${shareLink}`;
        } else {
          permalink = `https://www.facebook.com/dialog/send?link=${shareLink}&redirect_uri=${encodeURIComponent(window.location.origin)}`;
        }
        break;
      case 'whatsapp':
        permalink = `https://${isMobileOrTablet ? 'api' : 'web'}.whatsapp.com/send?text=${tagline}%20${shareLink}`;
        break;
      case 'telegram':
        permalink = `https://t.me/share/url?url=${shareLink}&text=${tagline}`;
        break;
      case 'threads':
        permalink = `https://www.threads.net/intent/post?text=${tagline}%20${shareLink}`;
        break;
      default:
        return;
    }

    window.open(permalink, '_blank');
  }

  /**
   * Handles the submit update attribution click event.
   * @param {Event} event - The event.
   */
  async handleSubmitUpdateAttribution(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const button = target.closest('button');

    const { sponsorIdLabel, enrollerIdLabel } = this.refs;
    const sponsorId = this.sponsorIdInput.value;
    const enrollerId = this.enrollerIdInput.value;
    
    this.clearInputsErrorState();

    if (!sponsorId || !enrollerId) {
      this.showAttributionError('EmptyInputs');
      if (!sponsorId) this.setInputErrorState(this.sponsorIdInput);
      if (!enrollerId) this.setInputErrorState(this.enrollerIdInput);
      return;
    }

    if (sponsorId === this.sponsorId && enrollerId === this.enrollerId) {
      this.updateModalState('default');
      this.hideAttributionError();
      return;
    }

    try {
      if (button) button.disabled = true;
      
      const isValid = await this.#validateSelectedResult({ sponsorId, enrollerId });
      if (!isValid) {
        this.showAttributionError('General');
        return;
      }

      this.sponsorId = sponsorId;
      this.enrollerId = enrollerId;

      if (sponsorIdLabel) sponsorIdLabel.textContent = sponsorId;
      if (enrollerIdLabel) enrollerIdLabel.textContent = enrollerId;

      if (button) button.disabled = false;
      this.updateModalState('default');
      this.hideAttributionError();

      // The saved cart stores sponsor/enroller, so re-save it with the new IDs.
      if (this.shareFrom === 'cart') {
        this.#saveSharedCart();
        // The focused Save button is now hidden and the content inert; keep focus inside the dialog.
        this.focus();
      }

    } catch (err) {
      console.log(err);

    } finally {
      if (button) button.disabled = false;
    }
  }

  /**
   * Validates the attribution sponsor/enroller.
   * @param {{ sponsorId: string, enrollerId: string }} data - The attribution data.
   */
  async #validateSelectedResult({ sponsorId, enrollerId }) {
    if (!this.backendClient) return false;

    try {
      const response = await this.backendClient.getReferralValidation(sponsorId, enrollerId);

      return (
        response.sponsor?.status === 'Valid' &&
        response.enroller?.status === 'Valid'
      );

    } catch (e) {
      console.error(e);
      return false;
    }
  }

  /**
   * Shows the update attribution error.
   * @param {string} type - The type of error.
   */
  showAttributionError(type) {
    const { attributionErrorContainer } = this.refs;
    if (!attributionErrorContainer) return;

    const attributionErrorMessages = attributionErrorContainer.querySelectorAll('[data-error-message]');
    attributionErrorMessages.forEach((message) => message.classList.add('hidden'));
    
    const targetAttributionErrorMessage = attributionErrorContainer.querySelector(`[data-error-message="${type}"]`);
    if (targetAttributionErrorMessage) {
      targetAttributionErrorMessage.classList.remove('hidden');
      attributionErrorContainer.classList.remove('hidden');
    }
  }

  /**
   * Hides the update attribution error.
   */
  hideAttributionError() {
    const { attributionErrorContainer } = this.refs;
    if (!attributionErrorContainer) return;

    const attributionErrorMessages = attributionErrorContainer.querySelectorAll('[data-error-message]');
    attributionErrorMessages.forEach((message) => message.classList.add('hidden'));
    attributionErrorContainer.classList.add('hidden');
  }

  /**
   * Sets the error state of an input.
   * @param {HTMLElement} input - The input element.
   */
  setInputErrorState(input) {
    input.classList.add('has-error');
  }

  /**
   * Clears the error state of inputs.
   */
  clearInputsErrorState() {
    this.sponsorIdInput.classList.remove('has-error');
    this.enrollerIdInput.classList.remove('has-error');
  }
  
  /**
   * Handles the cancel update attribution click event.
   */
  handleCancelUpdateAttribution() {
    this.updateModalState('default');
    this.hideAttributionError();
    this.clearInputsErrorState();

    this.sponsorIdInput.value = this.sponsorId || '';
    this.enrollerIdInput.value = this.enrollerId || '';
  }

  /**
   * Handles the update modal state click event.
   * @param {Event} event - The event.
   */
  handleUpdateModalState(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const button = target.closest('button');
    if (!button) return;
    
    const targetState = button.getAttribute('data-render-state');
    if (targetState) this.updateModalState(targetState);
  }

  /**
   * Updates modal state.
   * @param {string} state - The state name.
   */
  updateModalState(state) {
    const states = this.querySelectorAll('[data-modal-state]');
    states.forEach((elem) => elem.classList.add('hidden'));

    const targetState = this.querySelector(`[data-modal-state="${state}"]`);
    if (targetState) targetState.classList.remove('hidden');
  }
}

if (!customElements.get('share-modal')) {
  customElements.define('share-modal', ShareModal);
}